<template>
    <div class="audio-player-wrapper" :class="{ 'sent': isSent, 'deleted': isDeleted }">
        <audio v-liberar-midia 
            ref="audioElement" 
            :src="audioUrl" 
            preload="metadata"
            @timeupdate="updateProgress" 
            @ended="onEnded"
            @loadedmetadata="onLoadedMetadata"
        ></audio>
        
        <div class="audio-avatar-toggle">
            <template v-if="!isPlaying">
                <AvatarField 
                    class="audio-avatar" 
                    :foto="displayAvatar" 
                />
            </template>
            <template v-else>
                <button 
                    type="button" 
                    class="btn btn-playback-speed"
                    @click.stop="cyclePlaybackSpeed"
                    title="Mudar velocidade"
                >
                    {{ playbackRate.toFixed(1).replace('.0', '') }}x
                </button>
            </template>
        </div>
        
        <button @click.stop="togglePlay" type="button" class="btn btn-link btn-play-pause">
            <i :class="isPlaying ? 'ri-pause-fill' : 'ri-play-fill'"></i>
        </button>
        
        <div 
            class="audio-progress-bar" 
            ref="progressBar" 
            @click.stop="handleSeek"
        >
            <div class="waveform-track">
                <div 
                    class="waveform-bar --unplayed" 
                    v-for="(amp, i) in displayWaveform" 
                    :key="'bg-'+i" 
                    :style="{ height: Math.max(15, amp * 100) + '%' }"
                ></div>
            </div>
            
            <div 
                class="waveform-track --seek" 
                :style="{ clipPath: `inset(0 ${100 - progress}% 0 0)` }"
            >
                <div 
                    class="waveform-bar --played" 
                    v-for="(amp, i) in displayWaveform" 
                    :key="'fg-'+i" 
                    :style="{ height: Math.max(15, amp * 100) + '%' }"
                ></div>
            </div>
            
            <div class="progress-thumb" :style="{ left: progress + '%' }"></div>
        </div>
        
        <span class="audio-time">{{ computedDisplayTime }}</span>
        
    </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted, computed, onMounted, watch } from 'vue';
import AvatarField from '@/components/global/AvatarField.vue'; 

const props = defineProps({
    audioUrl: { type: String, required: true },
    duration: { type: String, required: false, default: '' },
    isSent: { type: Boolean, default: true },
    isDeleted: { type: Boolean, default: false }, 
    avatarUrl: { type: String, default: null },
    waveform: { type: Array as () => number[], default: () => [] }
});


const audioElement = ref<HTMLAudioElement | null>(null);
const progressBar = ref<HTMLElement | null>(null);


const displayAvatar = computed(() => {
    return props.isSent ? (props.avatarUrl || null) : props.avatarUrl;
});


const isPlaying = ref(false);
const currentTime = ref(0);
const progress = ref(0); // 0 a 100
const detectedDuration = ref(0); 
const playbackRate = ref(1.0);

const parseDurationToSeconds = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':').map(Number);
    if (parts.length === 2) {
        return (parts[0] * 60) + parts[1];
    }
    return 0;
};

const safeDuration = computed(() => {
    if (detectedDuration.value > 0 && isFinite(detectedDuration.value) && detectedDuration.value !== Infinity) {
        return detectedDuration.value;
    }
    return parseDurationToSeconds(props.duration);
});

// Waveform 
const fallbackWaveform = computed(() => {
    return [0.1, 0.2, 0.3, 0.4, 0.5, 0.4, 0.3, 0.2, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0.2, 0.3, 0.4, 0.5, 0.4, 0.3, 0.2, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2];
});

const displayWaveform = computed(() => {
    if (props.waveform && props.waveform.length > 0) {
        return props.waveform;
    }
    return fallbackWaveform.value;
});

//  Handlers 

const onLoadedMetadata = () => {
    if (audioElement.value && isFinite(audioElement.value.duration)) {
        detectedDuration.value = audioElement.value.duration;
    }
};

// Força reload do elemento <audio> quando a URL muda para evitar "no sources"
watch(() => props.audioUrl, (newUrl) => {
    if (!audioElement.value) return;

    // Reseta estado local
    isPlaying.value = false;
    progress.value = 0;
    currentTime.value = 0;
    detectedDuration.value = 0;

    try { audioElement.value.pause(); } catch {}

    // Atualiza a src explicitamente e força reload
    audioElement.value.src = newUrl || '';
    try { audioElement.value.load(); } catch {}
});

onMounted(() => {
    if (audioElement.value) {
        audioElement.value.playbackRate = playbackRate.value;
    }
});

const computedDisplayTime = computed(() => {

    if (isPlaying.value || currentTime.value > 0) {
        return formatTime(currentTime.value);
    }

    if (props.duration) {
        return props.duration;
    }

    return formatTime(safeDuration.value);
});


const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const cyclePlaybackSpeed = () => {
    if (!audioElement.value) return;
    let newRate = playbackRate.value;
    if (newRate === 1.0) { newRate = 1.5; }
    else if (newRate === 1.5) { newRate = 2.0; }
    else { newRate = 1.0; }
    playbackRate.value = newRate;
    audioElement.value.playbackRate = newRate;
};

const togglePlay = () => {
    if (!audioElement.value) return;

    if (isPlaying.value) {
        audioElement.value.pause();
    } else {
        audioElement.value.playbackRate = playbackRate.value;
        audioElement.value.play();
    }
    isPlaying.value = !isPlaying.value;
};

// Atualização do Progresso 
const updateProgress = () => {
    if (!audioElement.value) return;
    
    const current = audioElement.value.currentTime;
    const total = safeDuration.value; 
    
    currentTime.value = current;
    
    if (total > 0) {
        progress.value = (current / total) * 100;
    }
};

const onEnded = () => {
    isPlaying.value = false;
    progress.value = 0;
    currentTime.value = 0;
    if (audioElement.value) audioElement.value.currentTime = 0;
};

onUnmounted(() => {
    if (audioElement.value) {
        audioElement.value.pause();
    }
});

//  Lógica de Seek
const handleSeek = (event: MouseEvent) => {
    if (!progressBar.value || !audioElement.value) return;

    const total = safeDuration.value; // Usa a duração segura
    if (total <= 0) return;

    const rect = progressBar.value.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    
    // Garante que fique entre 0 e 1
    const seekPercentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = total * seekPercentage;
    
    if (isFinite(newTime)) {
        audioElement.value.currentTime = newTime;
        currentTime.value = newTime;
        progress.value = seekPercentage * 100;
    }
};
</script>

<style scoped>

.audio-player-wrapper {
    display: flex;
    align-items: center;
    gap: 8px; 
    padding: 6px;
    min-width: 295px;
    max-width: 100%;  
}

.audio-avatar-toggle { flex-shrink: 0; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; }
.audio-avatar { width: 36px; height: 36px; }
:deep(.audio-avatar img) { width: 36px !important; height: 36px !important; }
.btn-playback-speed { width: 80px; border-radius: 12px; padding: 0 6px; font-size: 12px; font-weight: 600; display: flex; align-items: center; justify-content: center; border: none; box-shadow: none !important; text-decoration: none !important; }
.btn-play-pause, .btn-play-pause:hover, .btn-play-pause:focus { flex-shrink: 0; font-size: 32px; padding: 0; margin: 0; line-height: 1; text-decoration: none !important; box-shadow: none !important; }

.audio-progress-bar { 
    flex-grow: 1; 
    position: relative; 
    height: 30px; 
    display: flex;
    align-items: center;
    cursor: pointer; 
    /* min-width: 100px; */
}
.progress-thumb { 
    position: absolute; 
    width: 12px; 
    height: 12px; 
    border-radius: 50%; 
    transform: translateX(-50%); 
    z-index: 3;
}
.waveform-track {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 2px; 
    padding: 0 2px;
    z-index: 1;
}
.waveform-track.--seek {
    z-index: 2;
}
.waveform-bar {
    width: 2px; 
    flex-shrink: 0;
    border-radius: 1px;
}

.audio-time { 
    font-size: 14px; 
    flex-shrink: 0; 
    font-variant-numeric: tabular-nums; 
    color: #ffffff8c;
}


[data-bs-theme=light] .audio-player-wrapper:not(.sent) .btn-play-pause { 
    color: #85677788 !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent) .audio-time { 
    color: rgba(120, 165, 151, 0.582) !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent) .progress-thumb { 
    background-color: #0fd197 !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent) .waveform-bar.--unplayed { 
    background-color: rgba(38, 165, 106, 0.3) !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent) .waveform-bar.--played { 
    background-color: var(--bs-green) !important; 
}

[data-bs-theme=light] .audio-player-wrapper:not(.sent) .btn-playback-speed {
    background-color: rgba(187, 175, 175, 0.185);
    color: #4d4248 ;
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .btn-play-pause { 
    color: #6c757d !important; /* Cinza escuro para contraste */
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .audio-time { 
    color: #6c757d !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .progress-thumb { 
    background-color: #6c757d !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .waveform-bar.--unplayed { 
    background-color: rgba(0, 0, 0, 0.1) !important; /* Cinza claro transparente */
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .waveform-bar.--played { 
    background-color: #6c757d !important; 
}
[data-bs-theme=light] .audio-player-wrapper:not(.sent).deleted .btn-playback-speed {
    background-color: rgba(0, 0, 0, 0.05) !important;
    color: #6c757d !important;
}
[data-bs-theme=light] .btn-play-pause { color: var(--bs-primary); }
[data-bs-theme=light] .progress-thumb { background-color: var(--bs-success); }
[data-bs-theme=light] .btn-playback-speed { background-color: var(--bs-border-color); color: var(--bs-text-muted); }
[data-bs-theme=light] .waveform-bar.--unplayed { background-color: rgba(var(--bs-success-rgb), 0.27); }
[data-bs-theme=light] .waveform-bar.--played { background-color: var(--bs-success); }
[data-bs-theme=light] .sent .btn-play-pause { color: var(--bs-primary); }
[data-bs-theme=light] .sent .progress-thumb { background-color: var(--bs-success); }
[data-bs-theme=light] .sent .btn-playback-speed { background-color: rgba(var(--bs-success-rgb), 0.27); color: var(--brand-success-hover); }
[data-bs-theme=light] .sent .waveform-bar.--unplayed { background-color: #b0fae65d; }
[data-bs-theme=light] .sent .waveform-bar.--played { background-color: var(--bs-success); }

[data-bs-theme=dark] .btn-play-pause { color: var(--brand-primary-light); }
[data-bs-theme=dark] .progress-thumb { background-color: rgba(255, 255, 255, 0.7); }
[data-bs-theme=dark] .btn-playback-speed { background-color: rgba(255, 255, 255, 0.15); color: rgba(255, 255, 255, 0.7); }
[data-bs-theme=dark] .waveform-bar.--unplayed { background-color: rgba(255, 255, 255, 0.15); }
[data-bs-theme=dark] .waveform-bar.--played { background-color: rgba(255, 255, 255, 0.7); }
[data-bs-theme=dark] .audio-time { color: rgba(255, 255, 255, 0.7); }
[data-bs-theme=dark] .sent .btn-play-pause { color: color-mix(in srgb, var(--brand-primary-light) 60%, white); }
[data-bs-theme=dark] .sent .progress-thumb { background-color: var(--bs-success); }
[data-bs-theme=dark] .sent .btn-playback-speed { background-color: rgba(var(--bs-success-rgb), 0.27); color: var(--brand-success-hover); }
[data-bs-theme=dark] .sent .waveform-bar.--unplayed { background-color: rgba(var(--bs-success-rgb), 0.27); }
[data-bs-theme=dark] .sent .waveform-bar.--played { background-color: var(--bs-success); }
[data-bs-theme=dark] .sent .audio-time { color: var(--bs-text-muted); }

</style>